import { useState } from 'react'
import { useAuthenticationDetails } from 'hooks/useAuthenticationDetails'
import MultiInventoryLocationsPicker from '../sharedComponents/MultiInventoryLocationsPicker'
import { getFilename, downloadPdf, SelectedInventoryLocation } from '../helpers/downloadHelpers'

import {
	EuiButton,
	EuiFormRow,
	EuiFlexGroup,
	EuiFlexItem,
	EuiHorizontalRule,
	EuiModalBody,
	EuiModalFooter,
	EuiRadio
} from '@equipmentshare/ds2'
import useShowError from 'hooks/useShowError'
import { heapEvent } from '@equipmentshare/heaper'

export type ExportCsvModalProps = {
	onCancel: () => void,
	onSuccess: () => void,
}

export default function ExportCsvModal( { onCancel, onSuccess }: ExportCsvModalProps ) {
	const showError = useShowError()
	const [ isLoading, setIsLoading ] = useState( false )
	const [ selectedInventoryLocationIds, setSelectedInventoryLocationIds ] = useState<string[]>( [] )
	const [ selectedInventoryLocations, setSelectedInventoryLocations ] = useState<SelectedInventoryLocation[]>( [] )
	const [ selectedDownloadType, setSelectedDownloadType ] = useState<'separate' | 'combined'>( 'combined' )
	const { accessToken } = useAuthenticationDetails()

	const onDownloadTypeChange = ( value: 'separate' | 'combined' ) => () => {
		setSelectedDownloadType( value )
	}

	const onDownloadClick = async() => {
		heapEvent( 'Inventory - Export CSV Modal - Click - Download', { selected_store_ids: selectedInventoryLocationIds.join( ',' ) } )
		setIsLoading( true )
		try {
			await downloadPdf( accessToken, selectedInventoryLocationIds, getFilename( selectedInventoryLocations, selectedDownloadType === 'combined' ), selectedDownloadType === 'combined' )
			setIsLoading( false )
			onSuccess()
		} catch ( e: any ) {
			setIsLoading( false )
			showError( 'ExportCsvModal', e?.message ?? 'Sorry, an error occurred' )
		}
	}

	const handleCancel = () => {
		heapEvent( 'Inventory - Export CSV Modal - Click - Cancel', {} )
		onCancel()
	}

	const isRadioDisabled = selectedInventoryLocationIds.length < 2

	return (
		<>
			<EuiModalBody>
				<div className="eui-fullHeight">
					<MultiInventoryLocationsPicker
						selectedIds={selectedInventoryLocationIds}
						setSelectedIds={setSelectedInventoryLocationIds}
						setSelectedInventoryLocations={setSelectedInventoryLocations}
					/>
					<EuiHorizontalRule />
					<EuiFormRow label="Download as" isDisabled={isRadioDisabled}>
						<EuiFlexGroup>
							<EuiFlexItem grow={false}>
								<EuiRadio
									id="combined"
									label="One combined spreadsheet"
									name="downloadType"
									onChange={onDownloadTypeChange( 'combined' )}
									checked={selectedDownloadType === 'combined'}
									disabled={isRadioDisabled}
								/>
							</EuiFlexItem>
							<EuiFlexItem grow={false}>
								<EuiRadio
									id="separate"
									label="Separate spreadsheets"
									name="downloadType"
									onChange={onDownloadTypeChange( 'separate' )}
									checked={selectedDownloadType === 'separate'}
									disabled={isRadioDisabled}
								/>
							</EuiFlexItem>
						</EuiFlexGroup>
					</EuiFormRow>
				</div>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					color="text"
					onClick={handleCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					fill
					isLoading={isLoading}
					isDisabled={selectedInventoryLocationIds.length < 1}
					onClick={onDownloadClick}
					id="downloadBtn"
				>
					Download
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
